import { Formik } from 'formik';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { getSelectedCountry } from '../../../../states/slices/resourceSlice';
import { getAllCompanyStaffById, updateBranch } from '../../../../api/requests';
import Input from '../../../../components/form-control/Input';
import Button from '../../../../components/form-control/Button';
import { useQuery } from '@tanstack/react-query';
import SelectDropdown, { ISelectDropdownItem } from '../../../../components/form-control/SelectDropdown';
import { getFormattedPhoneNumber, getPhoneNumberFromShortCodeFormat, phoneValidationSchema, showToast } from '../../../../utils/miscFunctions';
import useUserAccount from '../../../../hooks/userAccountHook';
import { IStaff } from '../../../../models/staff/IStaff';


interface IBranchProps {
  onClose: () => void;
  onProceed: () => void;
  active: boolean;
  address: string;
  email: string;
  name: string;
  phone: string;
  id: number;
  managerId: number;
}


interface IBranch {
  active: boolean;
  address: string;
  email: string;
  name: string;
  phone: string;
  managerId: number;
}

interface IBranchUpdatePayload {
  active: boolean;
  address: string;
  email: string;
  name: string;
  phone: string;
}

const EditBranch = (props: IBranchProps): JSX.Element => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1
  const [managers, setManagers] = useState<IStaff[]>([]);
  const [active, setActive] = useState<any>(['Yes', 'No']);
  const { user } = useUserAccount();
  const companyId = user?.company?.id; 
  const managersList = async () => {
    companyId && getAllCompanyStaffById(Number(companyId))
    .then((response) => {
      setManagers(response)
    }).catch((error) => {
    }).finally(() => {

    })
  }

  useQuery({ queryKey: ['managersList'], queryFn: managersList })
  return (
    <div className="py-6 px-3 md:px-6  w-full">
      <Formik
        initialValues={{
          active: props.active,
          address: props.address,
          email: props.email,
          name: props.name,
          phone: getPhoneNumberFromShortCodeFormat(props.phone),
          managerId: props.managerId,
        }}
        enableReinitialize={true}
        validateOnChange={true}
        validateOnBlur={true}
        validationSchema={Yup.object().shape({
          address: Yup.string().trim().required('Address is required.'),
          email: Yup.string().trim().required('Email is required.'),
          name: Yup.string().trim().required('Name is required.'),
          phone: phoneValidationSchema(),
          managerId: Yup.number().typeError('Branch manager is required.').required('Branch manager is required.'),
        })}

        onSubmit={async (values: IBranch, { resetForm }) => {
          const payload: IBranchUpdatePayload = {
            active: values.active,
            address: values.address,
            email: values.email,
            name: values.name,
            phone: getFormattedPhoneNumber(values.phone),
          };

          await updateBranch(payload, props.id, values.managerId)
            .then((response: any) => {
              showToast.success(response?.message || "Branch updated successfully")
            })
            .catch((error: any) => {
              console.log(error);
              showToast.error(error.description || "An error occurred");
            })
            .finally(() => {
              resetForm();
              props.onProceed();
            });
        }}>
        {({
          values,
          errors,
          isSubmitting,
          handleSubmit,
          handleChange,
          handleBlur,
          setFieldValue,
          touched
        }) => (
          <form className="" onSubmit={handleSubmit}>
            <div className="w-full flex flex-col space-y-8">
              {(user?.isBroker() || user?.isInternalAdmin()) && (
                <SelectDropdown
                  search={true}
                  label="Select Manager" 
                  name="managerId"
                  list={managers?.map((manager: any) => ({
                    name: `${manager.name}`,
                    value: manager.id
                  }))}
                  listSelectedValue={values.managerId}
                  onChange={(val: ISelectDropdownItem | null) => {
                    setFieldValue('managerId', val);
                  }}
                  onBlur={handleBlur}
                  placeholder="Select Manager"
                  error={touched.managerId ? errors.managerId : undefined}
                  helperText={touched?.managerId ? errors.managerId : undefined}
                  required
                />
              )}
              
              <Input
                type={'text'}
                name="address"
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
                label="Address"
                placeholder="Enter address"
                required
                error={touched.address ? errors.address : undefined}
                helperText={touched?.address ? errors.address : undefined}
                className="rounded"
              />

              <Input
                type={'text'}
                name="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                label="Email"
                placeholder="Enter email"
                required
                error={touched.email ? errors.email : undefined}
                helperText={touched?.email ? errors.email : undefined}
                className="rounded"
              />

              <Input
                type={'text'}
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                label="Branch Name"
                placeholder="Enter branch name"
                required
                error={touched.name ? errors.name : undefined}
                helperText={touched?.name ? errors.name : undefined}
                className="rounded"
              />

              <Input
                type={'text'}
                name="phone"
                value={values.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                label="Phone Number"
                placeholder="Enter phone number"
                required
                error={touched.phone ? errors.phone : undefined}
                helperText={touched?.phone ? errors.phone : undefined}
                className="rounded"
              />

              <SelectDropdown
                label="Active"
                name="active"
                list={active?.map((active: any) => ({
                  name: active,
                  value: active == 'Yes' ? true : false
                }))}
                listSelectedValue={values.active}
                onChange={(val: ISelectDropdownItem | null) => {
                  setFieldValue('active', val);
                }}
                onBlur={handleBlur}
                placeholder="Set Status"
                error={touched.active ? errors.active : undefined}
                helperText={touched?.active ? errors.active : undefined}
                required
              />

              <div className="w-full flex gap-4 mt-6 !mb-4">
                <Button
                  type="button"
                  label="Cancel"
                  onClick={props.onClose}
                  bgColor='secondary'
                  fullWidth
                  loading={submitting}
                  disabled={submitting}
                />
                <Button
                  label="Update Branch"
                  onClick={handleSubmit}
                  bgColor='primary'
              fullWidth
                  loading={submitting}
                  disabled={submitting}
                />
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default EditBranch;
